import React, { useState, useEffect } from "react";
import UserApiClient from "../../services/UserApiClient";

interface User {
  id: string;
  userName: string;
  password?: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  phone: string;
  isActive: boolean;
}

interface AddEditUserProps {
  isOpen: boolean;
  user: User | null;
  businessId: string;
  onClose: () => void;
  onSave: () => void;
}

const AddEditUser: React.FC<AddEditUserProps> = ({
  isOpen,
  user,
  businessId,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    role: "",
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [shouldShowPanel, setShouldShowPanel] = useState(false);

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // Limit to exactly 10 digits
    const limited = cleaned.substring(0, 10);

    // Apply formatting based on length
    if (limited.length >= 7) {
      return `(${limited.substring(0, 3)}) ${limited.substring(
        3,
        6
      )}-${limited.substring(6)}`;
    } else if (limited.length >= 4) {
      return `(${limited.substring(0, 3)}) ${limited.substring(3)}`;
    } else if (limited.length >= 1) {
      return `(${limited}`;
    }

    return limited;
  };

  useEffect(() => {
    if (user) {
      // Editing existing user
      setFormData({
        userName: user.userName || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone ? formatPhoneNumber(user.phone) : "",
        password: "", // Don't pre-fill password for security
        role: user.role || "",
        isActive: user.isActive,
      });
    } else {
      // Adding new user
      setFormData({
        userName: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        role: "",
        isActive: true,
      });
    }
    setError("");
    setSuccess("");
    setValidationErrors({});
  }, [user, isOpen]);

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Username validation
    if (!formData.userName.trim()) {
      errors.userName = "Username is required";
    } else if (formData.userName.length < 3) {
      errors.userName = "Username must be at least 3 characters";
    }

    // Password validation (only for new users)
    if (!user) {
      if (!formData.password.trim()) {
        errors.password = "Password is required";
      } else if (formData.password.length < 5) {
        errors.password = "Password must be at least 6 characters";
      }
    }

    // First name validation
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    // Role validation
    if (!formData.role.trim()) {
      errors.role = "Role is required";
    }

    // Email validation (optional but format validation if provided)
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    let formattedValue = value;
    if (name === "phone" && type !== "checkbox") {
      // Format phone number as user types
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : formattedValue,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before API call
    if (!validateForm()) {
      setError("Please fix the validation errors before submitting");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare data for API call (send only digits for phone)
      const apiData = {
        ...formData,
        phone: formData.phone.replace(/\D/g, ""), // Send only digits to API
        businessId,
      };

      let response;
      if (user) {
        // Update existing user
        response = await UserApiClient.updateUser(false, user.id, apiData);
      } else {
        // Create new user
        response = await UserApiClient.createUser(apiData);
      }

      // Show success message
      setSuccess(
        user ? "User updated successfully!" : "User created successfully!"
      );

      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (error: any) {
      console.error("Error saving user:", error);

      // Check if there's a specific error message from the API
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to save user";

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      userName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      role: "",
      isActive: true,
    });
    setError("");
    setSuccess("");
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={handleClose}></div>}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className={`slide-panel ${shouldShowPanel ? "slide-panel-open" : ""}`}
        >
        <div className="slide-panel-header">
          <h2>{user ? "Edit User" : "Add User"}</h2>
          <button className="slide-panel-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="slide-panel-content">
          {success && (
            <div className="success-message" style={{ margin: "0 0 1rem 0" }}>
              ✅ {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="settings-grid">
              <div className="setting-item">
                <label htmlFor="userName">Username *</label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.userName ? "form-input-error" : ""
                  }`}
                  required
                  disabled={!!user}
                />
                {validationErrors.userName && (
                  <span className="form-error">
                    {validationErrors.userName}
                  </span>
                )}
              </div>

              {!user && (
                <div className="setting-item">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`form-input ${
                      validationErrors.password ? "form-input-error" : ""
                    }`}
                    required
                    disabled={isLoading}
                    placeholder="Enter password"
                    minLength={5}
                  />
                  {validationErrors.password && (
                    <span className="form-error">
                      {validationErrors.password}
                    </span>
                  )}
                </div>
              )}

              <div className="setting-item">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.firstName ? "form-input-error" : ""
                  }`}
                  required
                  disabled={isLoading}
                />
                {validationErrors.firstName && (
                  <span className="form-error">
                    {validationErrors.firstName}
                  </span>
                )}
              </div>

              <div className="setting-item">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.lastName ? "form-input-error" : ""
                  }`}
                  required
                  disabled={isLoading}
                />
                {validationErrors.lastName && (
                  <span className="form-error">
                    {validationErrors.lastName}
                  </span>
                )}
              </div>

              <div className="setting-item">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.role ? "form-input-error" : ""
                  }`}
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Role</option>
                  <option value="Admin">Admin</option>
                  <option value="Admin">Manager</option>
                  <option value="POS">POS (API)</option>
                </select>
                {validationErrors.role && (
                  <span className="form-error">{validationErrors.role}</span>
                )}
              </div>

              {user && (
                <div className="setting-item full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="form-checkbox"
                      disabled={isLoading}
                    />
                    <span className="toggle-text">Active User</span>
                  </label>
                  <p
                    style={{
                      margin: "0.5rem 0 0 0",
                      fontSize: "0.875rem",
                      color: "#666",
                    }}
                  >
                    Inactive users cannot log in to the system
                  </p>
                </div>
              )}

              <div className="setting-item">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.email ? "form-input-error" : ""
                  }`}
                  disabled={isLoading}
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>

              <div className="setting-item">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`form-input ${
                    validationErrors.phone ? "form-input-error" : ""
                  }`}
                  placeholder="(232) 323-2323"
                  disabled={isLoading}
                />
                {validationErrors.phone && (
                  <span className="form-error">{validationErrors.phone}</span>
                )}
              </div>
            </div>

            {error && (
              <div className="user-list-error" style={{ margin: "1rem 0" }}>
                {error}
              </div>
            )}
          </form>
        </div>

        <div className="slide-panel-footer">
          <button
            type="button"
            className="btn secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : user ? "Update User" : "Add User"}
          </button>
        </div>
        </div>
      )}
    </>
  );
};

export default AddEditUser;
